import axios, { AxiosError } from 'axios'
import { handleError } from '@/utils/httpClient'
import { languageService } from '@/services/languageService'

function buildFileForm(file: File): FormData {
    const form = new FormData()
    form.append('file', file)
    return form
}

export interface ProofreadingAnalysisResult {
    isValid: boolean
    isLocked: boolean
    isUnreadable: boolean
    hasInvalidStructure: boolean
    hasComments: boolean
    unreviewedSegmentNumbers: number[]
    multilineSegmentNumbers: number[]
    unsupportedFormattingSegmentNumbers: number[]
    tabSegmentNumbers: number[]
    idmlUnsupportedStyleSegmentNumbers: number[]
    missingSegments: number
    addedSegments: number
    duplicateGroups: string[][]
    totalSegments: number
    updatedSegments: number
}

export const proofreadingApi = {

    cancelProofreading(documentId: number) {
        return axios.post(`proofreading/${documentId}/cancel`).catch(handleError);
    },

    resetProofreadingComplete(documentId: number) {
        return axios.post(`proofreading/${documentId}/reset`).catch(handleError);
    },

    analyzeImport(documentId: number, file: File): Promise<ProofreadingAnalysisResult | null> {
        return axios.post<ProofreadingAnalysisResult>(
            `proofreading/${documentId}/analyze`,
            buildFileForm(file)
        ).then(r => r.data).catch(handleError);
    },

    completeProofreading(documentId: number, file: File, mergeDuplicates = false): Promise<boolean | null> {
        return axios.post(`proofreading/${documentId}/complete`, buildFileForm(file), {
            params: { mergeDuplicates },
        })
            .then(() => true)
            .catch(handleError);
    },

    async exportForProofreading(documentId: number): Promise<void> {
        try {
            const response = await axios({
                url: `proofreading/${documentId}/export`,
                method: 'POST',
                responseType: 'blob',
                params: { lang: languageService.getLocale() },
            })

            const contentDisposition = response.headers['content-disposition']
            const filename = contentDisposition
                ? parseFileName(contentDisposition)
                : `proofreading_${documentId}.docx`

            triggerBrowserDownload(response.data, filename)
        } catch (error) {
            handleError(error as AxiosError)
        }
    },
}

function parseFileName(contentDisposition: string): string {
    const match = contentDisposition.match(/filename="?([^;"]+)"?/i)
    return match ? match[1].trim() : 'proofreading.docx'
}

function triggerBrowserDownload(data: Blob, filename: string): void {
    const href = window.URL.createObjectURL(data)
    const anchor = document.createElement('a')
    anchor.href = href
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(href)
}